import { supabase } from "~/integrations/supabase/client";
import { SalesCondition } from "~/types/client";

export interface DatabaseSalesCondition {
  id: string;
  name: string;
  type: string;
  maximum_amount: number | null;
  maximum_days: number | null;
  external_platform_name: string | null;
  created_at: string;
  updated_at: string;
  requires_prepayment: boolean | null;
  bsale_payment_id: number | null;
}

// Transform database format to frontend format
export function transformSalesCondition(
  condition: DatabaseSalesCondition
): SalesCondition {
  // Automatically set requiresPrepayment based on condition type and name
  const requiresPrepayment =
    condition.requires_prepayment ||
    condition.type === "prepaid" ||
    condition.name === "ANTICIPADO";

  return {
    id: condition.id,
    name: condition.name,
    type: condition.type as "prepaid" | "credit_line" | "external_platform",
    maximumAmount: condition.maximum_amount || undefined,
    maximumDays: condition.maximum_days || undefined,
    externalPlatformName: condition.external_platform_name || undefined,
    createdAt: condition.created_at,
    updatedAt: condition.updated_at,
    requiresPrepayment,
    bsalePaymentId: condition.bsale_payment_id ?? undefined,
  };
}

// Fetch all sales conditions from the database
export async function fetchSalesConditions(): Promise<SalesCondition[]> {
  try {
    const { data, error } = await supabase
      .from("sales_conditions")
      .select("*")
      .order("name");

    if (error) throw error;

    return (data as DatabaseSalesCondition[]).map(transformSalesCondition);
  } catch (error) {
    console.error("Error fetching sales conditions:", error);
    return [];
  }
}

// Fetch a single sales condition by ID
export async function fetchSalesConditionById(
  id: string
): Promise<SalesCondition | null> {
  try {
    const { data, error } = await supabase
      .from("sales_conditions")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;
    if (!data) return null;

    return transformSalesCondition(data as DatabaseSalesCondition);
  } catch (error) {
    console.error(`Error fetching sales condition with ID ${id}:`, error);
    return null;
  }
}

// Create a new sales condition
export async function createSalesCondition(
  salesCondition: Omit<SalesCondition, "id" | "createdAt" | "updatedAt">
): Promise<SalesCondition | null> {
  try {
    // Create the matching "forma de pago" in BSale first. If BSale fails we
    // abort (no DB row) so sales_conditions and BSale stay in sync — the
    // returned id feeds sales_conditions.bsale_payment_id, which create-factura
    // uses to pick the correct paymentTypeId at emission time.
    const { data: paymentType, error: paymentTypeError } =
      await supabase.functions.invoke("create-payment-type", {
        body: { name: salesCondition.name },
      });

    if (paymentTypeError || !paymentType?.success || !paymentType?.id) {
      // On a non-2xx response supabase-js nulls out `data` and replaces the
      // real message with a generic "Edge Function returned a non-2xx status
      // code". Read the actual BSale error out of the Response the
      // FunctionsHttpError carries so the user sees why it actually failed.
      let serverError: string | undefined;
      const context = (paymentTypeError as { context?: Response })?.context;
      if (context && typeof context.json === "function") {
        try {
          const body = await context.json();
          serverError = body?.error;
        } catch {
          // ignore — fall back to the generic message below
        }
      }

      throw new Error(
        paymentType?.error ||
          serverError ||
          paymentTypeError?.message ||
          "No se pudo crear la forma de pago en BSale"
      );
    }

    // Automatically set requires_prepayment based on condition type and name
    const requires_prepayment =
      salesCondition.requiresPrepayment ||
      salesCondition.type === "prepaid" ||
      salesCondition.name === "ANTICIPADO";

    const { data, error } = await supabase
      .from("sales_conditions")
      .insert({
        name: salesCondition.name,
        type: salesCondition.type,
        maximum_amount: salesCondition.maximumAmount || null,
        maximum_days: salesCondition.maximumDays || null,
        external_platform_name: salesCondition.externalPlatformName || null,
        requires_prepayment,
        bsale_payment_id: paymentType.id,
      })
      .select()
      .single();

    if (error) throw error;
    return transformSalesCondition(data as DatabaseSalesCondition);
  } catch (error) {
    console.error("Error creating sales condition:", error);
    // Re-throw so the hook can surface the specific reason (e.g. the BSale
    // error) and abort the create instead of showing a generic message.
    throw error;
  }
}

// Update an existing sales condition
export async function updateSalesCondition(
  id: string,
  salesCondition: Partial<
    Omit<SalesCondition, "id" | "createdAt" | "updatedAt">
  >
): Promise<SalesCondition | null> {
  try {
    const updates: any = {};
    if (salesCondition.name !== undefined) updates.name = salesCondition.name;
    if (salesCondition.type !== undefined) updates.type = salesCondition.type;
    if (salesCondition.maximumAmount !== undefined)
      updates.maximum_amount = salesCondition.maximumAmount;
    if (salesCondition.maximumDays !== undefined)
      updates.maximum_days = salesCondition.maximumDays;
    if (salesCondition.externalPlatformName !== undefined)
      updates.external_platform_name = salesCondition.externalPlatformName;

    // Handle requires_prepayment
    if (salesCondition.requiresPrepayment !== undefined) {
      updates.requires_prepayment = salesCondition.requiresPrepayment;
    } else if (
      salesCondition.type === "prepaid" ||
      salesCondition.name === "ANTICIPADO"
    ) {
      updates.requires_prepayment = true;
    }

    const { data, error } = await supabase
      .from("sales_conditions")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return transformSalesCondition(data as DatabaseSalesCondition);
  } catch (error) {
    console.error(`Error updating sales condition with ID ${id}:`, error);
    return null;
  }
}

// Delete a sales condition
export async function deleteSalesCondition(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("sales_conditions")
      .delete()
      .eq("id", id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error(`Error deleting sales condition with ID ${id}:`, error);
    return false;
  }
}
